import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { formatTime } from "@/utils/formatters";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useSettings } from "@/hooks/useValidations";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

const ACTION_CONFIG = {
  login: { label: "Login", color: "#007AFF" },
  validate: { label: "Validate", color: "#32D74B" },
  add_user: { label: "Add User", color: "#007AFF" },
  edit_user: { label: "Edit User", color: "#FF9F0A" },
  delete_user: { label: "Delete User", color: "#FF3B30" },
  hide_records: { label: "Hide Records", color: "#FF9F0A" },
  unhide_records: { label: "Unhide Records", color: "#32D74B" },
  delete_records: { label: "Delete Records", color: "#FF3B30" },
  import_backup: { label: "Import Backup", color: "#007AFF" },
  full_restore: { label: "Full Restore", color: "#FF3B30" },
  update_settings: { label: "Settings Change", color: "#FF9F0A" },
};

function ActionBadge({ action }) {
  const cfg = ACTION_CONFIG[action] || { label: action, color: "#737373" };
  return (
    <Badge className="font-semibold rounded-sm text-[10px] uppercase tracking-wider border-0" style={{ backgroundColor: cfg.color, color: "#000" }}>
      {cfg.label}
    </Badge>
  );
}

export function AuditLogTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState("all");
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({ user_name: "", user_email: "", target: "", start_date: "", end_date: "" });
  const [showFilters, setShowFilters] = useState(false);
  const settings = useSettings();
  const timezone = settings?.timezone || "UTC";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/audit-logs/actions");
        if (!cancelled) setActions(data);
      } catch (err) {
        console.error("Failed to load audit actions:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (actionFilter !== "all") params.set("action", actionFilter);
      if (filters.user_name) params.set("user_name", filters.user_name);
      if (filters.user_email) params.set("user_email", filters.user_email);
      if (filters.target) params.set("target", filters.target);
      if (filters.start_date) params.set("start_date", new Date(filters.start_date).toISOString());
      if (filters.end_date) params.set("end_date", new Date(filters.end_date + "T23:59:59").toISOString());
      const { data } = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    }
  }, [actionFilter, filters]);

  useEffect(() => { load(); }, [load]);

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-muted-foreground">{total} audit entries</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
            className={`border-[#262626] rounded-sm text-xs ${showFilters ? "text-[#007AFF] border-[#007AFF]" : "text-[#A3A3A3]"}`} data-testid="toggle-audit-filters-btn">
            <MagnifyingGlass size={14} className="mr-1" /> Filters {hasActiveFilters ? "(active)" : ""}
          </Button>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-48 bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs" data-testid="audit-filter">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[#262626]">
              <SelectItem value="all">All Actions</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_CONFIG[a]?.label || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 bg-[#121212] border border-[#262626] rounded-sm p-3" data-testid="audit-filters">
          <Input placeholder="User Name" value={filters.user_name} onChange={(e) => setFilters({ ...filters, user_name: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="audit-filter-user" />
          <Input placeholder="Email" value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="audit-filter-email" />
          <Input placeholder="Target" value={filters.target} onChange={(e) => setFilters({ ...filters, target: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8" data-testid="audit-filter-target" />
          <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" data-testid="audit-filter-start" />
          <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm text-xs h-8 font-mono-data" data-testid="audit-filter-end" />
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={() => setFilters({ user_name: "", user_email: "", target: "", start_date: "", end_date: "" })}
              className="text-[#FF3B30] hover:text-[#FF3B30] text-xs h-8" data-testid="audit-clear-filters">
              <X size={12} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      )}

      <div className="overflow-auto max-h-[55vh]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Time</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">User</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Details</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Target</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={6} className="text-center py-12 text-[#737373] text-sm">No audit entries</TableCell>
              </TableRow>
            ) : (
              logs.map((log, i) => (
                <TableRow key={`${log.timestamp}-${log.action}-${i}`} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
                  <TableCell className="font-mono-data text-xs text-[#737373] whitespace-nowrap">{formatTime(log.timestamp, timezone)}</TableCell>
                  <TableCell><ActionBadge action={log.action} /></TableCell>
                  <TableCell className="text-sm text-foreground">{log.user_name}</TableCell>
                  <TableCell className="font-mono-data text-xs text-muted-foreground">{log.user_email}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{log.details}</TableCell>
                  <TableCell className="font-mono-data text-xs text-muted-foreground">
                    {log.target_label ? (
                      <button
                        onClick={() => {
                          const tabMap = {
                            key_taken: "key-management", key_returned: "key-management",
                            key_edit: "key-management", key_delete: "key-management",
                            key_definition_add: "key-management", key_definition_delete: "key-management",
                            security_walkin_register: "security", security_check_in: "security", security_check_out: "security",
                            add_user: "users", delete_user: "users", edit_user: "users", reset_user_password: "users",
                          };
                          const tab = tabMap[log.action];
                          if (tab) {
                            window.dispatchEvent(new CustomEvent("admin-navigate", { detail: { tab, highlightId: log.target } }));
                          }
                        }}
                        className="text-left w-full hover:bg-[#0F0F0F] px-1 -mx-1 rounded-sm transition group"
                        data-testid={`audit-target-link-${log.target}`}
                        title="Open in module"
                      >
                        <div className="flex flex-col">
                          <span className="text-[#007AFF] group-hover:underline text-[11px]">{log.target_label} ↗</span>
                          <span className="text-[10px] text-[#525252]" title={log.target}>{log.target?.slice(0, 8)}…</span>
                        </div>
                      </button>
                    ) : (log.target || "-")}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
